import React, { useState } from 'react'
import { useGetTreeById } from 'shared/hooks/trees/hooks'
import { Box, Button, Link } from '@mui/material'
import { EditableCurriculaTree } from '../../tree/EditableCurriculaTree'
import { TreeIdInput } from '../../tree/navigationHelpers/TreeNodeRouter'
import {
  useSwapEditableTreeWithRefresh,
  useRemoveEditableTreeWithRefresh,
} from './utility'
import { useProductWithPrimaryTreeForId } from 'shared/api/tree'
import { useSetAtom } from 'infrastructure/state/jotai.wrapper'
import { globalDialogAtom } from 'shared/ui/GlobalDialog'

export function EditableTreeSwap({ productId }: { productId: number }) {
  const [swapping, setSwapping] = useState(false)
  const [treeIds, setTreeIds] = useState<{
    treeId?: string
    versionId?: string
  }>({})
  const swapFunc = useSwapEditableTreeWithRefresh()
  const removeFunc = useRemoveEditableTreeWithRefresh()
  const setDialog = useSetAtom(globalDialogAtom)
  const product = useProductWithPrimaryTreeForId(productId)
  const hasEditableTree = !!product?.editable_tree
  const label = hasEditableTree ? 'Swap Editable Tree' : 'Add Editable Tree'

  const { tree } = useGetTreeById(treeIds)

  const doRemove = async () => {
    const editableTreeId = Number(product?.editable_tree?.id)
    if (!editableTreeId) return
    if (!confirm('Are you sure you want to remove the editable tree?')) return
    const { errors } = await removeFunc(editableTreeId)
    if (errors?.length) {
      setDialog({
        title: 'Error Removing Editable Tree',
        contents: errors.map(
          (e: { message: string }) => e.message ?? 'An unknown error occurred.',
        ),
      })
    }
  }

  if (!swapping) {
    return (
      <Box>
        <Button onClick={() => setSwapping(true)}>{label}</Button>
        {hasEditableTree && (
          <Button color="error" onClick={doRemove}>
            Remove Editable Tree
          </Button>
        )}
      </Box>
    )
  }

  const cancel = () => {
    setTreeIds({})
    setSwapping(false)
  }

  const doIt = async () => {
    const { treeId, versionId } = treeIds
    if (!treeId || !versionId) return
    const result = await swapFunc(productId, { treeId, versionId })
    if (result.success) {
      cancel()
      return
    }
    const contents: React.ReactNode[] = []
    if (result.isDuplicate) {
      contents.push(
        'This tree is already assigned as an editable tree to another product. Please remove it from the other product first, then try again.',
      )
      if (result.conflictingProductId) {
        contents.push(
          <span key="link">
            Conflicting product:{' '}
            <Link
              href={`/products/${result.conflictingProductId}/show`}
              target="_blank"
              rel="noopener"
            >
              {result.conflictingProductTitle} (ID:{' '}
              {result.conflictingProductId})
            </Link>
          </span>,
        )
      }
    } else {
      contents.push(...result.messages)
    }
    setDialog({
      title: 'Error Swapping Editable Tree',
      contents,
    })
  }

  const TreeIds = () => {
    if (!treeIds.treeId) return null
    return <Box>{`${treeIds.treeId}:${treeIds.versionId}`}</Box>
  }

  if (tree) {
    return (
      <Box padding="5px" border="solid 1px">
        <Box>
          Swapping Editable Tree <Button onClick={cancel}>Cancel</Button>
          <Button onClick={doIt}>Do Swap</Button>
        </Box>
        <Box>Confirm info is correct for tree to swap to.</Box>
        <TreeIds />
        <Box>{tree.title}</Box>
        <EditableCurriculaTree tree={tree} />
      </Box>
    )
  }

  if (treeIds.treeId)
    return (
      <Box>
        <TreeIds />
        Loading...
      </Box>
    )

  return (
    <Box padding="5px" border="solid 1px">
      <Button onClick={cancel}>Cancel</Button>
      <Box>
        {`Paste in tree ids to swap to. You'll get a chance to edit metadata.`}
      </Box>
      <TreeIdInput
        onChange={({ treeId, version: versionId }) =>
          setTreeIds({ treeId, versionId })
        }
      />
    </Box>
  )
}
